import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { identity } from '@repletoryreach/sdk-api';
import { useAuth } from '@repletoryreach/sdk-auth';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Stack, Card, Title, Button, Group, Text, Badge } from '@repletoryreach/sdk-ui';
import { DataTable, Alert, EmptyState, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Drawer, Menu, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatRelative, formatDateTime } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { useT } from '../../../i18n/I18nProvider';
import { LOCALES, type Locale } from '../../../i18n/catalog';
import { TeamsCard } from './TeamsCard';

const PAGE_SIZE = 25;

export default function SettingsTeamPage() {
  const t = useT();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  // Roles (2 October 2026): owners and admins manage the team; only an owner
  // touches an owner. The service enforces the same; this only hides what would be refused.
  const canManage = me?.role == null || identity.isOrgAdmin(me.role);
  const isOwner = me?.role === 'owner';
  const [inviting, setInviting] = useState(false);
  const [query, setQuery] = useState('');
  const search = useDebounce(query, 250);
  const [page, setPage] = useState(1);

  const { data, isPending, error } = useQuery({
    queryKey: ['team', search, page],
    // §4 items 2 and 6: no limit meant the server default of 50, `meta.total`
    // was ignored, and an org with sixty people saw fifty with no warning and
    // no way to reach the rest. `search` and `offset` were already accepted.
    queryFn: () => identity.listUsers({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      ...(search.trim() ? { search: search.trim() } : {})
    }),
    placeholderData: (prev) => prev
  });

  useEffect(() => { setPage(1); }, [search]);

  const rows = (data?.data ?? []) as Indexed<identity.User>[];
  const total = data?.meta?.total ?? 0;

  const update = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      identity.updateUser(id, { is_active: active }),
    onSuccess: (_r, { active }) => {
      qc.invalidateQueries({ queryKey: ['team'] });
      notifySuccess(active ? t('settings.team.toast.restored') : t('settings.team.toast.suspended'), t('settings.nav.team'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.team'))
  });

  const changeRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: identity.OrgRole }) => identity.updateUser(id, { role }),
    onSuccess: (_r, { role }) => {
      qc.invalidateQueries({ queryKey: ['team'] });
      notifySuccess(t('settings.team.toast.roleChanged', { role: t(`roles.${role}`) }), t('settings.nav.team'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.team'))
  });

  const remove = useMutation({
    mutationFn: (u: identity.User) => identity.removeUser(u.id),
    onSuccess: (_r, u) => {
      qc.invalidateQueries({ queryKey: ['team'] });
      notifySuccess(t('settings.team.toast.removed', { email: u.email ?? '' }), t('settings.nav.team'));
    },
    onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('settings.nav.team'))
  });

  function confirmRemove(u: identity.User) {
    openConfirmModal({
      title: t('settings.team.modal.remove.title'),
      children: (
        <Stack gap={8}>
          <Text role="bodySmall">{t('settings.team.modal.remove.body', { email: u.email ?? '' })}</Text>
          {/* Being explicit that history survives is what makes this a safe
              button to press. */}
          <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.team.modal.remove.keeps')}</Text>
        </Stack>
      ),
      labels: { confirm: t('settings.team.actions.remove'), cancel: t('action.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => remove.mutate(u)
    });
  }

  const emptyInvite = canManage
    ? <Button size="xs" onClick={() => setInviting(true)}>{t('settings.team.actions.invite')}</Button>
    : undefined;

  return (
    <Stack gap="md">
      <Card withBorder data-guide="team-list">
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Title order={4}>{t('settings.nav.team')}</Title>
            <Group gap="xs">
              <TextInput
                type="search"
                aria-label={t('settings.team.search')}
                placeholder={t('settings.team.search.placeholder')}
                value={query}
                onChange={(e) => setQuery(e.currentTarget.value)}
              />
              {/* §4 item 10: this was hardcoded English while the key it needed
                  already existed and was used by the drawer it opens. */}
              {canManage && <Button size="sm" onClick={() => setInviting(true)} data-guide="team-invite">{t('settings.team.actions.invite')}</Button>}
            </Group>
          </Group>

          {error && <Alert color="error" role="alert">{t('settings.team.loadError')}</Alert>}

          {/* §4 item 12: the error Alert and the empty state could render at
              the same time, telling the user both that the load failed and
              that they have no teammates. */}
          {!isPending && !error && rows.length === 0 ? (
            <EmptyState
              title={search ? t('settings.team.emptySearch') : t('settings.team.empty')}
              description={search ? undefined : t('settings.team.empty.hint')}
              action={search
                ? <Button size="xs" variant="default" onClick={() => setQuery('')}>{t('inbox.filters.clear')}</Button>
                : emptyInvite}
            />
          ) : (
            <DataTable<Indexed<identity.User>>
              records={rows}
              loading={isPending}
              idAccessor="id"
              page={page}
              totalRecords={total}
              recordsPerPage={PAGE_SIZE}
              onPageChange={setPage}
              emptyState={t('settings.team.empty')}
              columns={[
                {
                  accessor: 'email',
                  title: t('common.email'),
                  render: (u) => (
                    <Group gap={6} wrap="nowrap">
                      <span>{u.email}</span>
                      {/* §4 item 8: nothing identified the current user, which
                          is how someone suspends themselves by accident. */}
                      {u.id === me?.id && <Badge size="xs" variant="light">{t('settings.team.you')}</Badge>}
                    </Group>
                  )
                },
                { accessor: 'display_name', title: t('common.name'), render: (u) => u.display_name ?? <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text> },
                {
                  accessor: 'role',
                  title: t('settings.team.role'),
                  // §4 item 4: no role column at all, though the identity
                  // service has org-scoped roles.
                  render: (u) => u.role
                    ? <Badge size="xs" variant="light" color={u.role === 'owner' ? 'teal' : u.role === 'admin' ? 'blue' : 'gray'}>{t(`roles.${u.role}`, u.role)}</Badge>
                    : <Text role="caption" c="var(--rr-color-text-muted)">{t('settings.team.noRole')}</Text>
                },
                {
                  accessor: 'locale',
                  title: t('common.language'),
                  // §4 item 9: an admin could not see what language a
                  // teammate's notifications arrive in.
                  render: (u) => LOCALES.find((l) => l.code === u.locale)?.label ?? u.locale
                },
                {
                  accessor: 'status',
                  title: t('common.status'),
                  render: (u) => u.is_locked
                    ? <StatusBadge status="inactive">{t('settings.team.suspended')}</StatusBadge>
                    : u.email_confirmed
                      ? <StatusBadge status="healthy">{t('status.active')}</StatusBadge>
                      // §4 item 7: with real invitations there is now a
                      // pending state, and it is the one an admin most often
                      // needs to see.
                      : <StatusBadge status="pending">{t('settings.team.pending')}</StatusBadge>
                },
                {
                  accessor: 'created_at',
                  title: t('settings.team.joined'),
                  // §4 item 11: an ISO date slice where a relative one reads
                  // better, with the exact value on hover.
                  render: (u) => (
                    <Tooltip label={formatDateTime(u.created_at)} position="top">
                      <span>{formatRelative(u.created_at)}</span>
                    </Tooltip>
                  )
                },
                {
                  accessor: 'actions',
                  title: t('common.actions'),
                  render: (u) => u.id === me?.id || !canManage || (u.role === 'owner' && !isOwner) ? <></> : (
                    <Menu>
                      <Menu.Target>
                        <Button size="xs" variant="subtle" aria-label={t('settings.team.actions.for', { email: u.email ?? '' })}>⋯</Button>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {/* Only an owner makes someone an owner (2 October 2026). */}
                        {(isOwner ? identity.ORG_ROLES : identity.ORG_ROLES.filter((r) => r !== 'owner'))
                          .filter((r) => r !== u.role)
                          .map((r) => (
                            <Menu.Item key={r} onClick={() => changeRole.mutate({ id: u.id, role: r })}>
                              {t('settings.team.actions.makeRole', { role: t(`roles.${r}`) })}
                            </Menu.Item>
                          ))}
                        <Menu.Divider />
                        {u.is_locked ? (
                          <Menu.Item onClick={() => update.mutate({ id: u.id, active: true })}>
                            {t('settings.team.actions.restore')}
                          </Menu.Item>
                        ) : (
                          <Menu.Item onClick={() => update.mutate({ id: u.id, active: false })}>
                            {t('settings.team.actions.suspend')}
                          </Menu.Item>
                        )}
                        <Menu.Divider />
                        <Menu.Item color="red" onClick={() => confirmRemove(u)}>
                          {t('settings.team.actions.remove')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  )
                }
              ]}
            />
          )}
        </Stack>
      </Card>

      {/* Teams such as Sales or Support (7 October 2026). */}
      <TeamsCard />

      <InviteDrawer
        opened={inviting}
        onClose={() => setInviting(false)}
        onInvited={() => qc.invalidateQueries({ queryKey: ['team'] })}
      />
    </Stack>
  );
}

function InviteDrawer({
  opened,
  onClose,
  onInvited
}: {
  opened: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [locale, setLocale] = useState<Locale>('en');
  const [role, setRole] = useState<identity.OrgRole>('agent');
  const [busy, setBusy] = useState(false);
  const [emailError, setEmailError] = useState('');
  const { user: me } = useAuth();

  useEffect(() => {
    if (opened) return;
    setEmail(''); setDisplayName(''); setLocale('en'); setRole('agent'); setEmailError('');
  }, [opened]);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setEmailError(t('settings.team.emailInvalid'));
      return;
    }

    setBusy(true);
    try {
      /*
       * §4 items 1 and 3: the console asked the admin to invent a password for
       * their teammate, enforced none of the rule it printed under the field,
       * and then sent no email, so the teammate never heard anything. The
       * service now emails a set-password link, so there is no password to
       * type here at all.
       */
      await identity.createUser({
        email: address,
        display_name: displayName.trim() || undefined,
        locale,
        role
      });
      notifySuccess(t('settings.team.toast.invited', { email: address }), t('settings.nav.team'));
      onInvited();
      onClose();
    } catch (err) {
      notifyError(err instanceof Error ? err.message : t('common.unknownError'), t('settings.team.toast.inviteFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer opened={opened} onClose={onClose} title={t('settings.team.actions.invite')} position="right" size="md">
      <form onSubmit={invite}>
        <Stack gap="md" style={{ padding: 4 }}>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.team.invite.hint')}</Text>

          <TextInput
            label={t('common.email')}
            type="email"
            error={emailError}
            value={email}
            onChange={(e) => { setEmail(e.currentTarget.value); setEmailError(''); }}
            required
            autoFocus
          />
          <TextInput
            // §4 item 15: this borrowed a key from the phone-numbers table.
            label={t('settings.profile.displayName')}
            description={t('settings.team.nameHint')}
            value={displayName}
            onChange={(e) => setDisplayName(e.currentTarget.value)}
          />
          <Select
            label={t('settings.team.role')}
            description={t(`roles.${role}.hint`)}
            value={role}
            onChange={(e) => setRole(e.currentTarget.value as identity.OrgRole)}
            data={(me?.role === 'owner' ? identity.ORG_ROLES : identity.ORG_ROLES.filter((r) => r !== 'owner'))
              .map((r) => ({ value: r, label: t(`roles.${r}`) }))}
          />
          <Select
            label={t('common.language')}
            description={t('settings.team.localeHint')}
            value={locale}
            onChange={(e) => setLocale(e.currentTarget.value as Locale)}
            data={LOCALES.map((l) => ({ value: l.code, label: l.label }))}
          />

          <Group justify="flex-end" style={{ paddingTop: 8, borderTop: '1px solid var(--rr-color-border)' }}>
            <Button type="button" variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button type="submit" loading={busy}>{t('settings.team.actions.sendInvite')}</Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
